import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext, API } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  LogOut, Users, Settings, Calendar, CheckCircle, XCircle,
  BarChart, Plus, FileText, ChevronLeft, ChevronRight,
  ClipboardList, GitBranch, ScrollText, Clock, Fingerprint, Coins, Plug, PiggyBank
} from 'lucide-react';
import { AdminStokvelTab } from '@/components/admin/stokvel/AdminStokvelTab';

import { UsersTab, TemplatesTab, RolesTab, BranchesTab, AuditTab } from '@/components/admin/SettingsTabs';
import { UserDialog, TemplateDialog, CloneShiftDialog, CreateShiftFromTemplateDialog, DirectCreateShiftDialog, AssignShiftDialog, EditShiftDialog, UnavailabilityDialog, RoleDialog, BranchDialog, SaveWeekAsTemplateDialog, ApplyTemplateWeekDialog, CreateTemplateWeekDialog, EditTemplateWeekDialog } from '@/components/admin/AdminDialogs';
import { AdminShiftsTab } from '@/components/admin/AdminShiftsTab';
import { AdminRequestsTab } from '@/components/admin/AdminRequestsTab';
import { AdminReportsTab } from '@/components/admin/AdminReportsTab';
import { AdminUnavailabilityTab } from '@/components/admin/AdminUnavailabilityTab';
import { AdminAttendanceTab } from '@/components/admin/AdminAttendanceTab';
import { AdminBiometricTab } from '@/components/admin/AdminBiometricTab';
import { AttendanceRulesTab } from '@/components/admin/AttendanceRulesTab';
import { GratuitiesTab } from '@/components/admin/GratuitiesTab';
import { IntegrationsTab } from '@/components/admin/IntegrationsTab';

export default function AdminDashboard() {
  const { user, logout } = useContext(AuthContext);
  const { tab: urlTab } = useParams();
  const navigate = useNavigate();
  // Whitelist of tab ids so a bad URL can't put the UI in a broken state.
  const ALLOWED_TABS = [
    'shifts', 'templates', 'requests', 'reports', 'unavailability',
    'users', 'roles', 'branches', 'audit', 'attendance', 'biometric',
    'attendance-rules', 'gratuities', 'integrations', 'stokvel',
  ];
  const initialTab = ALLOWED_TABS.includes(urlTab) ? urlTab : 'shifts';
  const [activeTab, setActiveTab] = useState(initialTab);
  // React to browser navigation (e.g. back button) that changes the :tab param.
  useEffect(() => {
    if (urlTab && ALLOWED_TABS.includes(urlTab) && urlTab !== activeTab) {
      setActiveTab(urlTab);
    }
  }, [urlTab]);
  const [autoScheduleResult, setAutoScheduleResult] = useState(null);
  const [showAutoScheduleResult, setShowAutoScheduleResult] = useState(false);
  const [autoScheduling, setAutoScheduling] = useState(false);
  const [users, setUsers] = useState([]);
  const [userFilter, setUserFilter] = useState('active');
  const [branches, setBranches] = useState([]);
  const [shiftTemplates, setShiftTemplates] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [shiftRequests, setShiftRequests] = useState([]);
  const [weeklyReport, setWeeklyReport] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState('');
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [filterFromTime, setFilterFromTime] = useState('');
  const [selectedWeek, setSelectedWeek] = useState(getMonday(new Date()));
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showEditUser, setShowEditUser] = useState(false);
  const [showCreateTemplate, setShowCreateTemplate] = useState(false);
  const [showEditTemplate, setShowEditTemplate] = useState(false);
  const [showCreateShift, setShowCreateShift] = useState(false);
  const [showEditShift, setShowEditShift] = useState(false);
  const [showAssignShift, setShowAssignShift] = useState(false);
  const [showUnavailability, setShowUnavailability] = useState(false);
  const [showCloneWeek, setShowCloneWeek] = useState(false);
  const [showCreateBranch, setShowCreateBranch] = useState(false);
  const [showEditBranch, setShowEditBranch] = useState(false);
  const [editingBranch, setEditingBranch] = useState(null);
  const [overrideHoursLimit, setOverrideHoursLimit] = useState(false);
  const [overrideUnavailability, setOverrideUnavailability] = useState(false);
  const [selectedStaffIds, setSelectedStaffIds] = useState([]);
  const [draggedTemplate, setDraggedTemplate] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [editingShift, setEditingShift] = useState(null);
  const [assigningShift, setAssigningShift] = useState(null);
  const [staffList, setStaffList] = useState([]);
  const [unavailability, setUnavailability] = useState([]);
  const [systemRoles, setSystemRoles] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditDateFrom, setAuditDateFrom] = useState('');
  const [auditDateTo, setAuditDateTo] = useState('');
  const [showCreateRole, setShowCreateRole] = useState(false);
  const [managersList, setManagersList] = useState([]);
  const [managerOnDuty, setManagerOnDuty] = useState(null);
  const [simplePayStatus, setSimplePayStatus] = useState(null);
  const [partialLeaveRecords, setPartialLeaveRecords] = useState([]);
  const [syncingSimplePay, setSyncingSimplePay] = useState(false);
  const [cloneSourceWeek, setCloneSourceWeek] = useState('');
  const [templateWeeks, setTemplateWeeks] = useState([]);
  const [showCreateTemplateWeek, setShowCreateTemplateWeek] = useState(false);
  const [showEditTemplateWeek, setShowEditTemplateWeek] = useState(null);
  const [showSaveWeekAsTemplate, setShowSaveWeekAsTemplate] = useState(false);
  const [showApplyTemplateWeek, setShowApplyTemplateWeek] = useState(false);
  const [cloneForm, setCloneForm] = useState({ source_date_start: '', source_date_end: '', target_date_start: '', clone_assignments: false });
  const [branchForm, setBranchForm] = useState({ name: '', color: '#808080', avg_labour_cost_per_hour: 0, cash_manager_branch_name: '' });
  const [formData, setFormData] = useState({
    username: '', password: '', first_name: '', last_name: '',
    role: 'staff', branch: '', branches: [], allowed_roles: [],
    email: '', employee_code: '', timetec_user_id: '', is_active: true,
    working_days_per_week: 5, include_in_gratuities: false, hourly_rate: 0
  });
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showCreateDirectShift, setShowCreateDirectShift] = useState(false);
  const [directShiftForm, setDirectShiftForm] = useState({
    shift_code: '', start_time: '07:00', end_time: '15:00',
    lunch_minutes: 30, max_capacity: 1, dates: []
  });
  const [templateForm, setTemplateForm] = useState({
    branch: '', shift_code: '', start_time: '07:00', end_time: '15:00',
    lunch_minutes: 30, role_name: '', color: '#808080', enabled: true
  });
  const [shiftForm, setShiftForm] = useState({ template_ids: [], dates: [] });

  // Undo/Redo stacks for shift actions
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const pushUndo = (action) => { setUndoStack(prev => [...prev.slice(-19), action]); setRedoStack([]); };

  const shiftCodes = systemRoles.map(r => r.code);
  const shiftCodeDetails = systemRoles.reduce((acc, role) => { acc[role.code] = role.role_name; return acc; }, {});

  // ─── Effects ───
  useEffect(() => { fetchUsers(); fetchBranches(); fetchSystemRoles(); fetchManagersList(); fetchTemplateWeeks(); initializeData(); }, []);
  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    setAuditDateFrom(thirtyDaysAgo); setAuditDateTo(today);
  }, []);
  useEffect(() => {
    if (activeTab === 'templates') { fetchShiftTemplates(); fetchTemplateWeeks(); }
    else if (activeTab === 'shifts') { fetchShifts(); fetchShiftTemplates(); fetchManagerOnDuty(); fetchUnavailability(); }
    else if (activeTab === 'requests') { fetchShiftRequests(); }
    else if (activeTab === 'reports') { fetchWeeklyReport(); }
    else if (activeTab === 'unavailability') { fetchUnavailability(); fetchSimplePayStatus(); fetchPartialLeaveRecords(); }
    else if (activeTab === 'roles') { fetchSystemRoles(); }
    else if (activeTab === 'branches') { fetchBranches(); }
    else if (activeTab === 'audit') { if (auditDateFrom && auditDateTo) fetchAuditLogs(); }
  }, [activeTab, selectedBranch, selectedWeek, auditDateFrom, auditDateTo]);

  // ─── Data Fetchers ───
  const fetchAuditLogs = async () => {
    try { const r = await axios.get(`${API}/admin/audit-logs`, { params: { date_from: auditDateFrom, date_to: auditDateTo } }); setAuditLogs(r.data); }
    catch { toast.error('Failed to fetch audit logs'); }
  };
  const fetchUsers = async () => {
    try {
      const r = await axios.get(`${API}/admin/users`); setUsers(r.data);
    } catch { toast.error('Failed to fetch users'); }
  };
  const fetchBranches = async () => {
    try { const r = await axios.get(`${API}/branches`); setBranches(r.data); if (r.data.length > 0 && !selectedBranch) setSelectedBranch(r.data[0].name); }
    catch { toast.error('Failed to fetch branches'); }
  };
  const fetchShiftTemplates = async () => {
    try { const r = await axios.get(`${API}/shift-templates`, { params: selectedBranch ? { branch: selectedBranch } : {} }); setShiftTemplates(r.data); }
    catch { toast.error('Failed to fetch shift templates'); }
  };
  const fetchShifts = async () => {
    try {
      const weekEnd = new Date(selectedWeek); weekEnd.setDate(weekEnd.getDate() + 6);
      const r = await axios.get(`${API}/shifts`, { params: { branch: selectedBranch, date_from: selectedWeek, date_to: weekEnd.toISOString().split('T')[0] } });
      // Normalize legacy shifts: ensure assigned_users array is populated
      const normalized = r.data.map(s => {
        if ((!s.assigned_users || s.assigned_users.length === 0) && s.assigned_user_id) {
          return { ...s, assigned_users: [{ user_id: s.assigned_user_id, username: s.assigned_username || '' }] };
        }
        return s;
      });
      setShifts(normalized);
    } catch { toast.error('Failed to fetch shifts'); }
  };
  const fetchShiftRequests = async () => {
    try { const r = await axios.get(`${API}/shift-requests`, { params: { branch: selectedBranch, status_filter: 'pending' } }); setShiftRequests(r.data); }
    catch { toast.error('Failed to fetch shift requests'); }
  };
  const fetchWeeklyReport = async () => {
    try { const r = await axios.get(`${API}/reports/weekly-hours`, { params: { week_start: selectedWeek, branch: selectedBranch } }); setWeeklyReport(r.data); }
    catch { console.error('Failed to fetch weekly report'); }
  };
  const fetchUnavailability = async () => {
    try { const weekDates = getWeekDates(); const r = await axios.get(`${API}/unavailability`, { params: { date_from: weekDates[0], date_to: weekDates[6] } }); setUnavailability(r.data); }
    catch { console.error('Failed to fetch unavailability'); }
  };
  const fetchSystemRoles = async () => {
    try { const r = await axios.get(`${API}/system-roles`); setSystemRoles(r.data); }
    catch { console.error('Failed to fetch system roles'); }
  };
  const fetchManagersList = async () => {
    try { const r = await axios.get(`${API}/managers-list`); setManagersList(r.data); }
    catch { console.error('Failed to fetch managers list'); }
  };
  const fetchManagerOnDuty = async () => {
    try { const r = await axios.get(`${API}/manager-on-duty`, { params: { week_start: selectedWeek } }); setManagerOnDuty(r.data); }
    catch { console.error('Failed to fetch manager on duty'); }
  };
  const fetchSimplePayStatus = async () => {
    try { const r = await axios.get(`${API}/admin/simplepay/status`); setSimplePayStatus(r.data); }
    catch { console.error('Failed to fetch SimplePay status'); }
  };
  const fetchPartialLeaveRecords = async () => {
    try { const r = await axios.get(`${API}/admin/simplepay/partial-leave`); setPartialLeaveRecords(r.data); }
    catch { console.error('Failed to fetch partial leave records'); }
  };
  const initializeData = async () => { try { await axios.post(`${API}/admin/init`); } catch { console.error('Init failed'); } };
  const fetchTemplateWeeks = async () => {
    try { const r = await axios.get(`${API}/template-weeks`); setTemplateWeeks(r.data); }
    catch { console.error('Failed to fetch template weeks'); }
  };
  const handleSaveWeekAsTemplate = async (name) => {
    if (!name.trim()) return;
    try {
      const r = await axios.post(`${API}/template-weeks/save-from-week`, null, { params: { name, branch: selectedBranch, week_start: selectedWeek } });
      toast.success(r.data.message); setShowSaveWeekAsTemplate(false); fetchTemplateWeeks();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to save week as template'); }
  };
  const handleCreateTemplateWeek = async (data) => {
    try {
      await axios.post(`${API}/template-weeks`, { name: data.name, branch: selectedBranch, shifts: data.shifts });
      toast.success('Template week created'); setShowCreateTemplateWeek(false); fetchTemplateWeeks();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to create template week'); }
  };
  const handleUpdateTemplateWeek = async (twId, data) => {
    try {
      await axios.put(`${API}/template-weeks/${twId}`, { name: data.name, shifts: data.shifts });
      toast.success('Template week updated'); setShowEditTemplateWeek(null); fetchTemplateWeeks();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to update template week'); }
  };
  const handleDeleteTemplateWeek = async (twId, twName) => {
    if (!confirm(`Delete template week '${twName}'?`)) return;
    try { await axios.delete(`${API}/template-weeks/${twId}`); toast.success('Template week deleted'); fetchTemplateWeeks(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed to delete template week'); }
  };
  const handleApplyTemplateWeek = async (twId) => {
    const weekEnd = new Date(selectedWeek);
    weekEnd.setDate(weekEnd.getDate() + 6);
    const existing = shifts.length;
    if (existing > 0 && !confirm(`This week already has ${existing} shift(s). The template shifts will be added alongside existing ones. Continue?`)) return;
    try {
      const r = await axios.post(`${API}/template-weeks/${twId}/apply`, null, { params: { week_start: selectedWeek } });
      toast.success(r.data.message); setShowApplyTemplateWeek(false); fetchShifts();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to apply template week'); }
  };

  // ─── Handlers ───
  const handleExportAuditPDF = () => {
    if (!auditDateFrom || !auditDateTo) { toast.error('Please select date range'); return; }
    const token = localStorage.getItem('token');
    window.open(`${API}/admin/audit-logs/pdf?date_from=${auditDateFrom}&date_to=${auditDateTo}&token=${token}`, '_blank');
  };
  const handleToggleUserActive = async (userId) => {
    try { const r = await axios.put(`${API}/admin/users/${userId}/toggle-active`); toast.success(r.data.message); fetchUsers(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed to toggle user status'); }
  };
  const createBranch = async () => {
    try { await axios.post(`${API}/branches`, branchForm); toast.success('Branch created'); fetchBranches(); setShowCreateBranch(false); setBranchForm({ name: '', color: '#808080', avg_labour_cost_per_hour: 0, cash_manager_branch_name: '' }); }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed to create branch'); }
  };
  const updateBranch = async () => {
    try { await axios.put(`${API}/branches/${editingBranch.id}`, branchForm); toast.success('Branch updated'); fetchBranches(); setShowEditBranch(false); setEditingBranch(null); setBranchForm({ name: '', color: '#808080', avg_labour_cost_per_hour: 0, cash_manager_branch_name: '' }); }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed to update branch'); }
  };
  const deleteBranch = async (branchId) => {
    if (!confirm('Delete this branch? This will fail if it has shifts, templates, or users.')) return;
    try { await axios.delete(`${API}/branches/${branchId}`); toast.success('Branch deleted'); fetchBranches(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed to delete branch'); }
  };
  const openEditBranch = (branch) => { setEditingBranch(branch); setBranchForm({ name: branch.name, color: branch.color || '#808080', avg_labour_cost_per_hour: branch.avg_labour_cost_per_hour || 0, cash_manager_branch_name: branch.cash_manager_branch_name || '' }); setShowEditBranch(true); };
  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) { await axios.put(`${API}/admin/users/${editingUser.id}`, formData); toast.success('User updated'); setShowEditUser(false); }
      else { await axios.post(`${API}/admin/users`, formData); toast.success('User created'); setShowCreateUser(false); }
      setFormData({ username: '', password: '', role: 'staff', branch: '', branches: [], allowed_roles: [], email: '', first_name: '', last_name: '', employee_code: '', timetec_user_id: '', is_active: true, can_work_evenings: false, working_days_per_week: 5, include_in_gratuities: false, hourly_rate: 0 });
      setEditingUser(null); fetchUsers();
    } catch (error) { toast.error(error.response?.data?.detail || `Failed to ${editingUser ? 'update' : 'create'} user`); }
  };
  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Delete this user?')) return;
    try { await axios.delete(`${API}/admin/users/${userId}`); toast.success('User deleted'); fetchUsers(); }
    catch { toast.error('Failed to delete user'); }
  };
  const handleEditUser = (u) => {
    // User settings now live on a dedicated full page. Keeping the state
    // setters for the welcome-email + delete legacy paths.
    navigate(`/admin/user/${u.id}`);
  };
  const handleCreateUserClick = () => navigate('/admin/user/new');
  const handleCreateTemplate = async (e) => {
    e.preventDefault();
    if (!templateForm.branch) { toast.error('Please select a branch'); return; }
    if (!templateForm.role_name) { toast.error('Please enter a role name'); return; }
    try {
      if (editingTemplate) { await axios.put(`${API}/shift-templates/${editingTemplate.id}`, templateForm); toast.success('Template updated'); }
      else { await axios.post(`${API}/shift-templates`, templateForm); toast.success('Template created'); }
      setTemplateForm({ branch: '', shift_code: '', start_time: '07:00', end_time: '15:00', lunch_minutes: 30, role_name: '', color: '#808080', enabled: true });
      setEditingTemplate(null); fetchShiftTemplates();
    } catch { toast.error(`Failed to ${editingTemplate ? 'update' : 'create'} template`); }
  };
  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateForm({ branch: template.branch, shift_code: template.shift_code, start_time: template.start_time, end_time: template.end_time, lunch_minutes: template.lunch_minutes || 0, role_name: template.role_name || '', color: template.color || '#808080', enabled: template.enabled });
    setShowEditTemplate(true);
  };
  const handleExportPDF = () => {
    const token = localStorage.getItem('token');
    const params = new URLSearchParams({ week_start: selectedWeek, token });
    if (selectedBranch) params.append('branch', selectedBranch);
    window.open(`${API}/reports/weekly-hours/pdf?${params.toString()}`, '_blank');
  };
  const handleExportShiftsPDF = () => {
    const token = localStorage.getItem('token');
    const params = new URLSearchParams({ week_start: selectedWeek, token });
    if (selectedBranch) params.append('branch', selectedBranch);
    window.open(`${API}/reports/shifts/pdf?${params.toString()}`, '_blank');
  };
  const handleAssignShiftMultiple = async () => {
    if (selectedStaffIds.length === 0) { toast.error('Please select at least one staff member'); return; }
    try {
      const r = await axios.post(`${API}/shifts/${assigningShift.id}/assign-multiple`, { user_ids: selectedStaffIds, override_hours_limit: overrideHoursLimit, override_unavailability: overrideUnavailability });
      toast.success(r.data.message);
      if (r.data.errors?.length > 0) r.data.errors.forEach(err => toast.warning(err));
      // Record undo for assigned users
      const assignedIds = r.data.assigned || selectedStaffIds;
      if (assignedIds.length > 0) {
        pushUndo({ type: 'assign', shiftId: assigningShift.id, userIds: assignedIds });
      }
      setShowAssignShift(false); setAssigningShift(null); setOverrideHoursLimit(false); setOverrideUnavailability(false); setSelectedStaffIds([]); fetchShifts();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to assign shift'); }
  };
  const toggleStaffSelection = (staffId) => { setSelectedStaffIds(prev => prev.includes(staffId) ? prev.filter(id => id !== staffId) : [...prev, staffId]); };
  const handleEditShift = async (shift) => { setEditingShift(shift); setShowEditShift(true); };
  const handleSaveShiftEdit = async () => {
    try {
      await axios.put(`${API}/shifts/${editingShift.id}`, {
        date: editingShift.date, start_time: editingShift.start_time, end_time: editingShift.end_time,
        shift_code: editingShift.shift_code, lunch_minutes: editingShift.lunch_minutes, max_capacity: editingShift.max_capacity
      });
      toast.success('Shift updated'); setShowEditShift(false); setEditingShift(null); fetchShifts();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to update shift'); }
  };
  const handleUnassignShift = async (shiftId) => {
    if (!window.confirm('Remove this staff member from the shift?')) return;
    try { await axios.post(`${API}/shifts/${shiftId}/unassign`); toast.success('Staff removed'); setShowEditShift(false); setEditingShift(null); fetchShifts(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed to unassign shift'); }
  };
  const handleUnassignUser = async (shiftId, userId) => {
    try {
      // Get shift data before unassigning for undo
      const shiftData = shifts.find(s => s.id === shiftId);
      const userInfo = shiftData?.assigned_users?.find(u => u.user_id === userId);
      await axios.post(`${API}/shifts/${shiftId}/unassign?user_id=${userId}`);
      toast.success('Staff member removed');
      pushUndo({ type: 'unassign', shiftId, userId, username: userInfo?.username });
      fetchShifts();
    }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed to remove staff member'); }
  };
  const handleDeleteShift = async (shiftId, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this shift?')) return;
    try {
      // Save shift data for undo before deleting
      const shiftData = shifts.find(s => s.id === shiftId);
      await axios.delete(`${API}/shifts/${shiftId}`);
      toast.success('Shift deleted');
      if (shiftData) pushUndo({ type: 'delete_shift', shiftData: { ...shiftData } });
      fetchShifts();
    }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed to delete shift'); }
  };
  const handleAutoSchedule = async () => {
    if (!window.confirm('Auto-assign staff to all open shifts this week across all branches?\n\nRules applied:\n• Fewest hours first (even distribution)\n• Max 45 weekly hours\n• Max 6 days per week\n• 12-hour rest between shifts\n• No split shifts\n• Respects availability & qualifications')) return;
    setAutoScheduling(true);
    try {
      const r = await axios.post(`${API}/admin/auto-schedule`, { week_start: selectedWeek });
      setAutoScheduleResult(r.data);
      setShowAutoScheduleResult(true);
      toast.success(r.data.message);
      fetchShifts();
    } catch (error) { toast.error(error.response?.data?.detail || 'Auto-schedule failed'); }
    finally { setAutoScheduling(false); }
  };
  const handleRemoveAutoSchedule = async () => {
    if (!window.confirm('Remove all auto-scheduled assignments for this week?\n\nManual assignments will be preserved.')) return;
    setAutoScheduling(true);
    try {
      const r = await axios.post(`${API}/admin/auto-schedule/remove`, { week_start: selectedWeek });
      toast.success(r.data.message);
      fetchShifts();
      setAutoScheduleResult(null);
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to remove auto-schedule'); }
    finally { setAutoScheduling(false); }
  };
  const handleRefreshAutoSchedule = async () => {
    if (!window.confirm('Refresh auto-schedule?\n\nThis will remove all auto-assigned staff and re-run the scheduler.\nManual assignments will be preserved.')) return;
    setAutoScheduling(true);
    try {
      await axios.post(`${API}/admin/auto-schedule/remove`, { week_start: selectedWeek });
      const r = await axios.post(`${API}/admin/auto-schedule`, { week_start: selectedWeek });
      setAutoScheduleResult(r.data);
      setShowAutoScheduleResult(true);
      toast.success(r.data.message);
      fetchShifts();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to refresh auto-schedule'); }
    finally { setAutoScheduling(false); }
  };
  const openAssignDialog = async (shift) => {
    setAssigningShift(shift); setOverrideHoursLimit(false);
    try {
      const r = await axios.get(`${API}/users/available-for-shift`, { params: { date: shift.date, start_time: shift.start_time, end_time: shift.end_time, branch: shift.branch, shift_id: shift.id } });
      const eligible = r.data.filter(u => u.role === 'staff' && u.branches.includes(shift.branch) && u.allowed_roles.includes(shift.shift_code));
      setStaffList(eligible); setShowAssignShift(true);
      if (eligible.length === 0) toast.warning('No available staff for this shift time.');
    } catch { toast.error('Failed to fetch available staff'); }
  };
  const handleCloneWeek = async () => {
    if (!cloneForm.source_date_start || !cloneForm.target_date_start) { toast.error('Please select source and target dates'); return; }
    try {
      const r = await axios.post(`${API}/shifts/clone-week`, { source_date_start: cloneForm.source_date_start, source_date_end: cloneForm.source_date_end || null, target_date_start: cloneForm.target_date_start, branch: selectedBranch, clone_assignments: cloneForm.clone_assignments });
      toast.success(r.data.message + (r.data.with_assignments ? ' (with staff assignments)' : ''));
      setShowCloneWeek(false); setCloneForm({ source_date_start: '', source_date_end: '', target_date_start: '', clone_assignments: false }); fetchShifts();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to clone shifts'); }
  };
  const handleAddUnavailability = async (data) => {
    try {
      const r = await axios.post(`${API}/unavailability`, data);
      if (r.data.created_count) {
        toast.success(`Created ${r.data.created_count} recurring unavailability entries`);
      } else {
        toast.success('Unavailability added');
        if (r.data.warning) toast.warning(r.data.warning, { duration: 6000 });
      }
      fetchUnavailability(); fetchShifts(); setShowUnavailability(false);
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to add unavailability'); }
  };
  const handleDeleteUnavailability = async (id) => {
    if (!window.confirm('Remove this unavailability period?')) return;
    try { await axios.delete(`${API}/unavailability/${id}`); toast.success('Unavailability removed'); fetchUnavailability(); }
    catch { toast.error('Failed to remove unavailability'); }
  };
  const handleSetManagerOnDuty = async (managerId) => {
    if (!managerId) { setManagerOnDuty(null); return; }
    try { const r = await axios.post(`${API}/manager-on-duty`, { week_start: selectedWeek, manager_id: managerId }); setManagerOnDuty(r.data); toast.success('Manager on duty updated'); }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed to set manager on duty'); }
  };
  const handleSimplePaySync = async () => {
    setSyncingSimplePay(true);
    try { const r = await axios.post(`${API}/admin/simplepay/sync`); if (r.data.success) { toast.success(r.data.message); fetchUnavailability(); fetchPartialLeaveRecords(); fetchSimplePayStatus(); } else { toast.error(r.data.message); } }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed to sync from SimplePay'); }
    finally { setSyncingSimplePay(false); }
  };
  const handleCompletePartialLeave = async (recordId, startTime, endTime) => {
    try { await axios.post(`${API}/admin/simplepay/partial-leave/${recordId}/complete`, null, { params: { start_time: startTime, end_time: endTime } }); toast.success('Partial leave completed'); fetchPartialLeaveRecords(); fetchUnavailability(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed to complete partial leave'); }
  };
  const handleCreateRole = async (data) => {
    try { await axios.post(`${API}/system-roles`, data); toast.success('Role created'); fetchSystemRoles(); setShowCreateRole(false); }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed to create role'); }
  };
  const handleDeleteRole = async (roleId, roleCode) => {
    if (!window.confirm(`Delete role '${roleCode}'?`)) return;
    try { await axios.delete(`${API}/system-roles/${roleId}`); toast.success('Role deleted'); fetchSystemRoles(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed to delete role'); }
  };
  const handleToggleTemplate = async (templateId, enabled) => {
    try { await axios.put(`${API}/shift-templates/${templateId}`, { enabled }); toast.success(`Template ${enabled ? 'enabled' : 'disabled'}`); fetchShiftTemplates(); }
    catch { toast.error('Failed to update template'); }
  };
  const handleDeleteTemplate = async (templateId, templateName) => {
    if (!window.confirm(`Delete the ${templateName} template?`)) return;
    try { await axios.delete(`${API}/shift-templates/${templateId}`); toast.success('Template deleted'); fetchShiftTemplates(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed to delete template'); }
  };
  const handleReorderTemplate = async (templateId, direction) => {
    try { await axios.post(`${API}/shift-templates/${templateId}/reorder?direction=${direction}`); fetchShiftTemplates(); }
    catch { console.error('Failed to reorder'); }
  };
  const handleCreateShifts = async (e) => {
    e.preventDefault();
    if (shiftForm.template_ids.length === 0) { toast.error('Please select at least one template'); return; }
    if (shiftForm.dates.length === 0) { toast.error('Please select at least one date'); return; }
    try {
      const createdIds = [];
      for (const templateId of shiftForm.template_ids) { for (const date of shiftForm.dates) { const r = await axios.post(`${API}/shifts`, { template_id: templateId, date }); if (r.data?.id) createdIds.push(r.data.id); } }
      toast.success(`Created ${createdIds.length} shifts from ${shiftForm.template_ids.length} template(s)`);
      if (createdIds.length > 0) pushUndo({ type: 'create_shifts', shiftIds: createdIds });
      setShowCreateShift(false); setShiftForm({ template_ids: [], dates: [] }); fetchShifts();
    } catch { toast.error('Failed to create shifts'); }
  };
  const handleCreateDirectShifts = async (e) => {
    e.preventDefault();
    if (!directShiftForm.shift_code) { toast.error('Please select a shift code'); return; }
    if (directShiftForm.dates.length === 0) { toast.error('Please select at least one date'); return; }
    try {
      const r = await axios.post(`${API}/shifts/direct`, { shift_code: directShiftForm.shift_code, start_time: directShiftForm.start_time, end_time: directShiftForm.end_time, lunch_minutes: parseInt(directShiftForm.lunch_minutes) || 0, max_capacity: parseInt(directShiftForm.max_capacity) || 1, dates: directShiftForm.dates, branch: selectedBranch });
      toast.success(`Created ${directShiftForm.dates.length} shift(s)`);
      const createdIds = r.data?.created_ids || [];
      if (createdIds.length > 0) pushUndo({ type: 'create_shifts', shiftIds: createdIds });
      setShowCreateDirectShift(false); setDirectShiftForm({ shift_code: '', start_time: '07:00', end_time: '15:00', lunch_minutes: 30, max_capacity: 1, dates: [] }); fetchShifts();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to create shifts'); }
  };
  const handleShiftRequest = async (requestId, action) => {
    try { await axios.post(`${API}/shift-requests/action`, { request_id: requestId, action }); toast.success(`Request ${action}d`); fetchShiftRequests(); fetchShifts(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed to process request'); }
  };
  const toggleBranch = (branchName) => { setFormData(prev => ({ ...prev, branches: prev.branches.includes(branchName) ? prev.branches.filter(b => b !== branchName) : [...prev.branches, branchName] })); };
  const toggleRole = (roleCode) => { setFormData(prev => ({ ...prev, allowed_roles: prev.allowed_roles.includes(roleCode) ? prev.allowed_roles.filter(r => r !== roleCode) : [...prev.allowed_roles, roleCode] })); };

  // Undo/Redo handlers
  const handleUndo = async () => {
    if (undoStack.length === 0) return;
    const action = undoStack[undoStack.length - 1];
    setUndoStack(prev => prev.slice(0, -1));
    try {
      if (action.type === 'assign') {
        // Undo assign: remove users from shift
        for (const uid of action.userIds) { await axios.post(`${API}/shifts/${action.shiftId}/unassign?user_id=${uid}`); }
        toast.success('Undo: staff unassigned');
        setRedoStack(prev => [...prev, action]);
      } else if (action.type === 'unassign') {
        // Undo unassign: re-assign user to shift
        await axios.post(`${API}/shifts/${action.shiftId}/assign-multiple`, { user_ids: [action.userId], override_hours_limit: true, override_unavailability: true });
        toast.success('Undo: staff re-assigned');
        setRedoStack(prev => [...prev, action]);
      } else if (action.type === 'create_shifts') {
        // Undo create: delete shifts
        for (const sid of action.shiftIds) { try { await axios.delete(`${API}/shifts/${sid}`); } catch {} }
        toast.success(`Undo: ${action.shiftIds.length} shift(s) deleted`);
        setRedoStack(prev => [...prev, action]);
      } else if (action.type === 'delete_shift') {
        // Undo delete: recreate shift
        const s = action.shiftData;
        const r = await axios.post(`${API}/shifts/direct`, { shift_code: s.shift_code, start_time: s.start_time, end_time: s.end_time, lunch_minutes: s.lunch_minutes || 0, max_capacity: s.max_capacity || 1, dates: [s.date], branch: s.branch });
        const newId = r.data?.created_ids?.[0];
        // Re-assign users if any were assigned
        if (newId && s.assigned_users?.length > 0) {
          const uids = s.assigned_users.map(u => u.user_id);
          await axios.post(`${API}/shifts/${newId}/assign-multiple`, { user_ids: uids, override_hours_limit: true, override_unavailability: true });
        }
        toast.success('Undo: shift restored');
        setRedoStack(prev => [...prev, { ...action, restoredShiftId: newId }]);
      }
      fetchShifts();
    } catch (error) { toast.error('Undo failed: ' + (error.response?.data?.detail || error.message)); }
  };
  const handleRedo = async () => {
    if (redoStack.length === 0) return;
    const action = redoStack[redoStack.length - 1];
    setRedoStack(prev => prev.slice(0, -1));
    try {
      if (action.type === 'assign') {
        // Redo assign: re-assign users
        await axios.post(`${API}/shifts/${action.shiftId}/assign-multiple`, { user_ids: action.userIds, override_hours_limit: true, override_unavailability: true });
        toast.success('Redo: staff assigned');
        setUndoStack(prev => [...prev, action]);
      } else if (action.type === 'unassign') {
        // Redo unassign: remove user again
        await axios.post(`${API}/shifts/${action.shiftId}/unassign?user_id=${action.userId}`);
        toast.success('Redo: staff removed');
        setUndoStack(prev => [...prev, action]);
      } else if (action.type === 'create_shifts') {
        // Redo create: recreate shifts (cannot perfectly restore, but re-fetch handles it)
        toast.info('Redo: please recreate the shifts');
      } else if (action.type === 'delete_shift') {
        // Redo delete: delete the restored shift
        const sid = action.restoredShiftId || action.shiftData?.id;
        if (sid) { await axios.delete(`${API}/shifts/${sid}`); }
        toast.success('Redo: shift deleted');
        setUndoStack(prev => [...prev, action]);
      }
      fetchShifts();
    } catch (error) { toast.error('Redo failed: ' + (error.response?.data?.detail || error.message)); }
  };

  // Handle override assignment from auto-schedule suggestions
  const handleOverrideAssign = async (shiftId, userId) => {
    try {
      const r = await axios.post(`${API}/shifts/${shiftId}/assign-multiple`, { user_ids: [userId], override_hours_limit: true, override_unavailability: true });
      toast.success(r.data.message || 'Staff assigned (override)');
      pushUndo({ type: 'assign', shiftId, userIds: [userId] });
      // Update the autoScheduleResult to remove this option
      if (autoScheduleResult) {
        const updated = { ...autoScheduleResult, override_options: autoScheduleResult.override_options?.map(slot => slot.shift_id === shiftId ? { ...slot, options: slot.options.filter(o => o.user_id !== userId), filled: true, filled_by: slot.options.find(o => o.user_id === userId)?.username } : slot) };
        setAutoScheduleResult(updated);
      }
      fetchShifts();
    } catch (error) { toast.error(error.response?.data?.detail || 'Override assign failed'); }
  };



  // Drag handlers for templates
  const handleDragStart = (e, template) => { setDraggedTemplate(template); e.dataTransfer.effectAllowed = 'move'; };
  const handleDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };
  const handleDrop = async (e, targetTemplate) => {
    e.preventDefault();
    if (!draggedTemplate || draggedTemplate.id === targetTemplate.id) return;
    const draggedIndex = shiftTemplates.findIndex(t => t.id === draggedTemplate.id);
    const targetIndex = shiftTemplates.findIndex(t => t.id === targetTemplate.id);
    try {
      const dir = draggedIndex < targetIndex ? 'down' : 'up';
      const count = Math.abs(targetIndex - draggedIndex);
      for (let i = 0; i < count; i++) { await axios.post(`${API}/shift-templates/${draggedTemplate.id}/reorder?direction=${dir}`); }
      toast.success('Template reordered');
    } catch { toast.error('Failed to reorder template'); }
    setDraggedTemplate(null); fetchShiftTemplates();
  };
  const handleDragEnd = () => { setDraggedTemplate(null); };

  // Bulk reorder for shifts (smooth drag-and-drop)
  const handleBulkReorder = async (orderUpdates) => {
    try {
      await axios.post(`${API}/shifts/bulk-reorder`, { items: orderUpdates });
      fetchShifts();
    } catch { toast.error('Failed to reorder shifts'); }
  };

  // ─── Utilities ───
  function getMonday(date) {
    const d = new Date(date); const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff)).toISOString().split('T')[0];
  }
  const getWeekDates = () => {
    const dates = []; const start = new Date(selectedWeek);
    for (let i = 0; i < 7; i++) { const date = new Date(start); date.setDate(start.getDate() + i); dates.push(date.toISOString().split('T')[0]); }
    return dates;
  };
  const changeWeek = (direction) => { const current = new Date(selectedWeek); current.setDate(current.getDate() + (direction * 7)); setSelectedWeek(current.toISOString().split('T')[0]); };
  const getISOWeekNumber = (dateStr) => {
    const date = new Date(dateStr); const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7; d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  };

  // Shared context for child components
  const ctx = {
    user, users, branches, systemRoles, shiftTemplates, shiftCodes,
    setShowCreateUser, setShowEditUser, setShowCreateTemplate, setShowEditTemplate,
    setShowCreateRole, setShowCreateBranch, setShowEditBranch,
    setShowCloneWeek, setShowCreateShift, setShowCreateDirectShift,
    setShowAssignShift, setShowUnavailability,
    setSelectedStaffForShift: setSelectedStaffIds, setOverrideHoursLimit, setOverrideUnavailability,
    setEditingUser, setEditingTemplate, setEditingShift, setEditingBranch,
    setBranchForm, setFormData, setTemplateForm,
    setDirectShiftForm, setShiftForm, setCloneForm,
    setUserFilter, setSelectedBranch, setAuditDateFrom, setAuditDateTo,
    setSelectedRoles,
    showCreateUser, showEditUser, showCreateTemplate, showEditTemplate,
    showCreateRole, showCreateBranch, showEditBranch,
    showCloneWeek, showCreateShift, showCreateDirectShift,
    showAssignShift, showUnavailability, showEditShift, setShowEditShift,
    selectedStaffForShift: selectedStaffIds, overrideHoursLimit, overrideUnavailability, staffList,
    editingUser, editingTemplate, editingShift, editingBranch,
    branchForm, formData, templateForm, directShiftForm, shiftForm, cloneForm,
    userFilter, selectedBranch, assigningShift,
    auditLogs, auditDateFrom, auditDateTo,
    draggedTemplate,
    handleCreateUser, handleEditUser, handleCreateUserClick, handleDeleteUser, handleToggleUserActive,
    handleCreateTemplate, handleEditTemplate, handleDeleteTemplate, handleToggleTemplate,
    handleCreateShifts, handleCreateDirectShifts,
    handleCloneWeek, handleAssignShiftMultiple, handleSaveShiftEdit, handleUnassignShift, handleUnassignUser,
    handleAddUnavailability, handleCreateRole, handleDeleteRole,
    handleExportAuditPDF, handleExportShiftsPDF, handleExportPDF,
    createBranch, updateBranch, deleteBranch, openEditBranch,
    fetchUsers, fetchAuditLogs,
    toggleBranch, toggleRole,
    handleDragStart, handleDragOver, handleDrop, handleDragEnd,
    getWeekDates, getISOWeekNumber, selectedWeek, changeWeek,
    // Shifts tab specific
    shifts, selectedRoles, filterFromTime, setFilterFromTime,
    weeklyReport, handleDeleteShift, openAssignDialog, handleEditShift: handleEditShift,
    managerOnDuty, managersList, handleSetManagerOnDuty,
    handleShiftDragStart: null, handleShiftDragOver: null, handleShiftDrop: null, handleShiftDragEnd: null,
    draggedShift: null, handleBulkReorder,
    handleShiftRequest,
    // Auto Schedule
    handleAutoSchedule, autoScheduling, autoScheduleResult, showAutoScheduleResult, setShowAutoScheduleResult,
    handleRemoveAutoSchedule, handleRefreshAutoSchedule,
    // Undo/Redo
    undoStack, redoStack, handleUndo, handleRedo,
    // Override assign from auto-schedule
    handleOverrideAssign, setAutoScheduleResult,
    // Template Weeks
    templateWeeks, showCreateTemplateWeek, setShowCreateTemplateWeek,
    showEditTemplateWeek, setShowEditTemplateWeek,
    showSaveWeekAsTemplate, setShowSaveWeekAsTemplate,
    showApplyTemplateWeek, setShowApplyTemplateWeek,
    handleSaveWeekAsTemplate, handleCreateTemplateWeek, handleUpdateTemplateWeek,
    handleDeleteTemplateWeek, handleApplyTemplateWeek,
    // Unavailability tab specific
    unavailability, handleDeleteUnavailability,
    simplePayStatus, syncingSimplePay, handleSimplePaySync,
    partialLeaveRecords, handleCompletePartialLeave,
    // Requests
    shiftRequests,
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex">
      {/* Sidebar */}
      <div className={`${sidebarCollapsed ? 'w-16' : 'w-48'} bg-gray-800 text-white transition-all duration-300 flex flex-col fixed h-full z-20`}>
        <div className={`p-3 border-b border-gray-700 flex items-center ${sidebarCollapsed ? 'justify-center' : 'justify-between'}`}>
          <img
            src={sidebarCollapsed
              ? '/tmc-icon.png'
              : 'https://www.themerchantcoffee.co.za/merchant%20white%20logo_No%20Strap%20-1-.png'}
            alt="The Merchant Coffee"
            className={`${sidebarCollapsed ? 'h-8 w-8 object-contain bg-white rounded-lg p-1' : 'h-9 w-auto'}`}
          />
          {!sidebarCollapsed ? (
            <button onClick={() => setSidebarCollapsed(!sidebarCollapsed)} className="p-1 hover:bg-gray-700 rounded">
              <ChevronLeft className="w-4 h-4" />
            </button>
          ) : (
            <button onClick={() => setSidebarCollapsed(!sidebarCollapsed)} className="p-1 hover:bg-gray-700 rounded ml-1">
              <ChevronRight className="w-4 h-4" />
            </button>
          )}
        </div>

        <nav className="flex-1 py-4 overflow-y-auto">
          <ul className="space-y-1 px-2">
            {[
              { id: 'shifts', icon: Calendar, label: 'Shifts' },
              { id: 'requests', icon: CheckCircle, label: 'Requests', badge: shiftRequests.length },
              { id: 'reports', icon: BarChart, label: 'Reports' },
              { id: 'unavailability', icon: XCircle, label: 'Unavailability' },
              { id: 'attendance', icon: Clock, label: 'Attendance' },
              { id: 'biometric', icon: Fingerprint, label: 'Biometric' },
              { id: 'gratuities', icon: Coins, label: 'Gratuities' },
              { id: 'stokvel', icon: PiggyBank, label: 'Stokvel' },
            ].map(item => (
              <li key={item.id}>
                <button onClick={() => setActiveTab(item.id)} data-testid={`tab-${item.id}`} title={sidebarCollapsed ? item.label : ''}
                  className={`w-full flex items-center ${sidebarCollapsed ? 'justify-center' : ''} gap-3 px-3 py-2.5 rounded-lg transition ${activeTab === item.id ? 'bg-gray-600 text-white' : 'text-gray-300 hover:bg-gray-700 hover:text-white'}`}>
                  <item.icon className="w-5 h-5 flex-shrink-0" />
                  {!sidebarCollapsed && (<><span className="flex-1 text-left text-sm">{item.label}</span>{item.badge > 0 && <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">{item.badge}</span>}</>)}
                  {sidebarCollapsed && item.badge > 0 && <span className="absolute left-12 top-1 bg-red-500 text-white text-xs w-5 h-5 flex items-center justify-center rounded-full">{item.badge}</span>}
                </button>
              </li>
            ))}
            <li className="pt-4">
              <button onClick={() => !sidebarCollapsed && setSettingsOpen(!settingsOpen)} title={sidebarCollapsed ? 'Settings' : ''}
                className={`w-full flex items-center ${sidebarCollapsed ? 'justify-center' : ''} gap-3 px-3 py-2.5 rounded-lg transition ${['users', 'templates', 'roles', 'branches', 'audit', 'attendance-rules', 'integrations'].includes(activeTab) ? 'bg-gray-600 text-white' : 'text-gray-300 hover:bg-gray-700 hover:text-white'}`}>
                <Settings className="w-5 h-5 flex-shrink-0" />
                {!sidebarCollapsed && (<><span className="flex-1 text-left text-sm">Settings</span><ChevronRight className={`w-4 h-4 transition-transform ${settingsOpen ? 'rotate-90' : ''}`} /></>)}
              </button>
              {!sidebarCollapsed && settingsOpen && (
                <ul className="mt-1 ml-4 space-y-1 border-l border-gray-600 pl-3">
                  {[{ id: 'users', icon: Users, label: 'Users' }, { id: 'templates', icon: ClipboardList, label: 'Templates' }, { id: 'roles', icon: FileText, label: 'Staff Roles' }, { id: 'branches', icon: GitBranch, label: 'Branches' }, { id: 'attendance-rules', icon: Clock, label: 'Attendance Rules' }, { id: 'integrations', icon: Plug, label: 'Integrations' }, { id: 'audit', icon: ScrollText, label: 'Audit Log' }].map(item => (
                    <li key={item.id}>
                      <button onClick={() => setActiveTab(item.id)} data-testid={`tab-${item.id}`}
                        className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg transition text-sm ${activeTab === item.id ? 'bg-gray-600 text-white' : 'text-gray-400 hover:bg-gray-700 hover:text-white'}`}>
                        <item.icon className="w-4 h-4 flex-shrink-0" /><span>{item.label}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {sidebarCollapsed && (
                <ul className="mt-2 space-y-1">
                  {[{ id: 'users', icon: Users, label: 'Users' }, { id: 'templates', icon: ClipboardList, label: 'Templates' }, { id: 'roles', icon: FileText, label: 'Staff Roles' }, { id: 'branches', icon: GitBranch, label: 'Branches' }, { id: 'attendance-rules', icon: Clock, label: 'Attendance Rules' }, { id: 'integrations', icon: Plug, label: 'Integrations' }, { id: 'audit', icon: ScrollText, label: 'Audit Log' }].map(item => (
                    <li key={item.id}>
                      <button onClick={() => setActiveTab(item.id)} data-testid={`tab-${item.id}`} title={item.label}
                        className={`w-full flex items-center justify-center py-2 rounded-lg transition ${activeTab === item.id ? 'bg-gray-600 text-white' : 'text-gray-400 hover:bg-gray-700 hover:text-white'}`}>
                        <item.icon className="w-4 h-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          </ul>
        </nav>

        <div className="p-4 border-t border-gray-700">
          {!sidebarCollapsed && (<div className="mb-3 text-sm"><div className="text-white font-medium truncate">{user.username}</div><div className="text-gray-400 text-xs capitalize">{user.role}</div></div>)}
          <Button onClick={logout} variant="ghost" data-testid="logout-button"
            className={`w-full flex items-center gap-2 text-gray-300 hover:text-white hover:bg-gray-700 ${sidebarCollapsed ? 'justify-center px-2' : ''}`} size="sm">
            <LogOut className="w-4 h-4" />{!sidebarCollapsed && <span>Logout</span>}
          </Button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className={`flex-1 ${sidebarCollapsed ? 'ml-16' : 'ml-48'} transition-all duration-300`}>
        <div className="px-4 sm:px-6 lg:px-8 py-6">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <UsersTab ctx={ctx} />
            <TemplatesTab ctx={ctx} />
            <TabsContent value="shifts"><AdminShiftsTab ctx={ctx} /></TabsContent>
            <TabsContent value="requests"><AdminRequestsTab ctx={ctx} /></TabsContent>
            <TabsContent value="reports"><AdminReportsTab ctx={ctx} /></TabsContent>
            <TabsContent value="unavailability"><AdminUnavailabilityTab ctx={ctx} /></TabsContent>
            <TabsContent value="attendance"><AdminAttendanceTab ctx={ctx} /></TabsContent>
            <TabsContent value="biometric"><AdminBiometricTab ctx={ctx} /></TabsContent>
            <TabsContent value="attendance-rules"><AttendanceRulesTab /></TabsContent>
            <TabsContent value="gratuities"><GratuitiesTab /></TabsContent>
            <TabsContent value="stokvel"><AdminStokvelTab /></TabsContent>
            <TabsContent value="integrations"><IntegrationsTab /></TabsContent>
            <RolesTab ctx={ctx} />
            <BranchesTab ctx={ctx} />
            <AuditTab ctx={ctx} />
          </Tabs>
        </div>
      </div>

      <UserDialog ctx={ctx} />
      <TemplateDialog ctx={ctx} />
      <CloneShiftDialog ctx={ctx} />
      <CreateShiftFromTemplateDialog ctx={ctx} />
      <DirectCreateShiftDialog ctx={ctx} />
      <AssignShiftDialog ctx={ctx} />
      <EditShiftDialog ctx={ctx} />
      <UnavailabilityDialog ctx={ctx} />
      <RoleDialog ctx={ctx} />
      <BranchDialog ctx={ctx} />
      <SaveWeekAsTemplateDialog ctx={ctx} />
      <ApplyTemplateWeekDialog ctx={ctx} />
      <CreateTemplateWeekDialog ctx={ctx} />
      <EditTemplateWeekDialog ctx={ctx} />
    </div>
  );
}
